import React, { useEffect, useState } from "react";
import { Alert, Text, View } from "react-native";
import { Edit3, RefreshCw, Save, Search, Trash2, Upload } from "lucide-react-native";
import { api } from "../api/client";
import { ActionMenu, Badge, Button, Card, DataTable, Field, IconButton, ScreenHeader, Select, Toolbar } from "../components/UI";
import { colors, styles } from "../styles";
import { isAdmin as checkAdmin } from "../utils/format";

const empty = {
  item_code: "",
  item_name: "",
  item_description: "",
  short_description: "",
  long_description: "",
  unit: "Nos",
  actual_price: "0",
  unit_price: "0",
  brand: "",
  model: "",
  status: "active",
  main_category_id: "",
  sub_category_id: "",
  display_order: "0",
  notes: "",
  cost_review_required: 0
};

const toText = (value, fallback = "") => (value === null || value === undefined ? fallback : String(value));

export function ProductsScreen({ user }) {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState({ main_categories: [], sub_categories: [] });
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeMessage, setCodeMessage] = useState("");
  const [codeManualEdit, setCodeManualEdit] = useState(false);
  const isAdmin = checkAdmin(user);

  const load = () => api.products(q).then(setRows).catch((error) => Alert.alert("Products", error.message));

  useEffect(() => {
    load();
    api.categories().then(setCategories);
  }, []);

  const beginEdit = (row) => {
    setMessage("");
    setCodeMessage("");
    setCodeManualEdit(false);
    setForm({
      id: row.id,
      item_code: toText(row.item_code),
      item_name: toText(row.item_name),
      item_description: toText(row.item_description),
      short_description: toText(row.short_description),
      long_description: toText(row.long_description),
      unit: toText(row.unit, "Nos"),
      actual_price: toText(row.actual_price, "0"),
      unit_price: toText(row.unit_price, "0"),
      brand: toText(row.brand),
      model: toText(row.model),
      status: toText(row.status, "active"),
      main_category_id: toText(row.main_category_id),
      sub_category_id: toText(row.sub_category_id),
      display_order: toText(row.display_order, "0"),
      notes: toText(row.notes),
      cost_review_required: row.cost_review_required ? 1 : 0
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const categoryName = (id) => categories.main_categories.find((category) => String(category.id) === String(id))?.name || "";
  const subCategoryName = (id) => categories.sub_categories.find((category) => String(category.id) === String(id))?.name || "";

  const generateProductCode = async ({ mainCategoryId = form.main_category_id, subCategoryId = form.sub_category_id, force = false } = {}) => {
    if (!mainCategoryId) {
      setCodeMessage("Select Main Category to generate ProductCode.");
      setForm((current) => current.id ? current : { ...current, item_code: "" });
      return;
    }
    if (form.id && !force) return;
    if (form.id && force) {
      const ok = typeof confirm === "function"
        ? confirm("Changing ProductCode may affect quotation imports and product references. Continue?")
        : true;
      if (!ok) return;
    }

    setCodeLoading(true);
    setCodeMessage("Generating code...");
    try {
      const result = await api.nextProductCode(categoryName(mainCategoryId), subCategoryName(subCategoryId), mainCategoryId, subCategoryId);
      setForm((current) => ({ ...current, item_code: result.nextProductCode || "" }));
      setCodeManualEdit(false);
      setCodeMessage("Auto generated from selected category. Code will be checked again on save.");
    } catch (error) {
      setCodeMessage(error.message || "Could not generate product code. Please try again.");
    } finally {
      setCodeLoading(false);
    }
  };

  const chooseMainCategory = async (mainCategoryId) => {
    setForm((current) => ({
      ...current,
      main_category_id: mainCategoryId,
      sub_category_id: "",
      item_code: current.id ? current.item_code : ""
    }));
    setCodeManualEdit(false);
    if (!form.id && mainCategoryId) await generateProductCode({ mainCategoryId, subCategoryId: "", force: true });
  };

  const chooseSubCategory = async (subCategoryId) => {
    setForm((current) => ({ ...current, sub_category_id: subCategoryId }));
    if (!form.id && form.main_category_id) await generateProductCode({ mainCategoryId: form.main_category_id, subCategoryId, force: true });
  };

  const save = async () => {
    if (saving) return;
    if (!form.item_name.trim()) {
      setMessage("Item name is required.");
      return;
    }

    setSaving(true);
    setMessage("");
    try {
      await api.saveProduct({
        ...form,
        main_category_id: form.main_category_id ? Number(form.main_category_id) : null,
        sub_category_id: form.sub_category_id ? Number(form.sub_category_id) : null,
        actual_price: Number(form.actual_price || 0),
        unit_price: Number(form.unit_price || 0),
        display_order: Number(form.display_order || 0),
        cost_review_required: form.cost_review_required ? 1 : 0
      });
      setForm(empty);
      setCodeManualEdit(false);
      setCodeMessage("");
      await load();
      setMessage("Product saved successfully.");
    } catch (error) {
      setMessage(error.message || "Product could not be saved.");
      Alert.alert("Product Save Failed", error.message || "Product could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (confirm("Delete this product?")) {
      await api.deleteProduct(id);
      load();
    }
  };

  const importMissingProducts = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportSummary(null);
    setMessage("");
    try {
      const summary = await api.importMissingProducts(file);
      setImportSummary(summary);
      await load();
      setMessage(`Import completed: ${summary.products_inserted || 0} inserted, ${summary.duplicates_skipped || 0} duplicates skipped.`);
    } catch (error) {
      setMessage(error.message || "Missing products import failed.");
      Alert.alert("Import Missing Products", error.message || "Missing products import failed.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <View style={styles.page}>
      <ScreenHeader title="Products Catalog" subtitle="Maintain product master data for quotation item selection" />

      {isAdmin ? (
        <Card>
          <Text style={styles.h2}>Import Missing Products CSV</Text>
          <Text style={styles.small}>Add-only import. Existing ProductCodes are skipped; existing products & quotations are preserved.</Text>
          <View style={styles.row}>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              disabled={importing}
              onChange={importMissingProducts}
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: "12px",
                padding: "10px 14px",
                backgroundColor: "#fff",
                minWidth: 280,
                fontSize: "14px"
              }}
            />
            <Button icon={Upload} variant="secondary" disabled>{importing ? "Importing..." : "Import Missing Products CSV"}</Button>
          </View>

          {importSummary ? (
            <View style={styles.row}>
              <Badge variant="info">Rows processed: {importSummary.rows_processed || 0}</Badge>
              <Badge variant="success">Inserted: {importSummary.products_inserted || 0}</Badge>
              <Badge variant="warning">Duplicates skipped: {importSummary.duplicates_skipped || 0}</Badge>
            </View>
          ) : null}
        </Card>
      ) : null}

      <Card>
        <Text style={styles.h2}>{form.id ? "Edit Product" : "Add New Product"}</Text>
        <View style={styles.row}>
          <Select label="Main Category" value={form.main_category_id} onChangeText={chooseMainCategory}>
            <option value="">Select Main Category...</option>
            {categories.main_categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select label="Sub Category" value={form.sub_category_id} onChangeText={chooseSubCategory}>
            <option value="">Select Sub Category...</option>
            {categories.sub_categories.filter((s) => !form.main_category_id || String(s.main_category_id) === String(form.main_category_id)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Field label="ProductCode" value={codeLoading ? "Generating code..." : form.item_code} editable={codeManualEdit && isAdmin} onChangeText={(v) => setForm({ ...form, item_code: v.toUpperCase() })} />
          <Button icon={RefreshCw} variant="secondary" disabled={!form.main_category_id || codeLoading} onPress={() => generateProductCode({ force: true })}>Refresh Code</Button>
          {isAdmin ? <Button variant="secondary" onPress={() => setCodeManualEdit(!codeManualEdit)}>{codeManualEdit ? "Lock Code" : "Manual Edit"}</Button> : null}
        </View>
        {codeMessage ? <Text style={styles.small}>{codeMessage}</Text> : null}

        <View style={styles.row}>
          <Field label="Item Name *" value={form.item_name} onChangeText={(v) => setForm({ ...form, item_name: v })} style={{ flex: 2 }} />
          <Field label="Unit" value={form.unit} onChangeText={(v) => setForm({ ...form, unit: v })} />
          <Field label="Actual Cost Price (Internal)" value={form.actual_price} onChangeText={(v) => setForm({ ...form, actual_price: v })} keyboardType="numeric" />
          <Field label="Sale Price / Unit Price *" value={form.unit_price} onChangeText={(v) => setForm({ ...form, unit_price: v })} keyboardType="numeric" />
        </View>

        <View style={styles.row}>
          <Field label="Brand / Company" value={form.brand} onChangeText={(v) => setForm({ ...form, brand: v })} />
          <Field label="Model No" value={form.model} onChangeText={(v) => setForm({ ...form, model: v })} />
          <Select label="Actual Cost Review" value={form.cost_review_required ? "1" : "0"} onChangeText={(v) => setForm({ ...form, cost_review_required: v === "1" ? 1 : 0 })}>
            <option value="0">Not Required</option>
            <option value="1">Needs Cost Review</option>
          </Select>
          <Select label="Status" value={form.status} onChangeText={(v) => setForm({ ...form, status: v })}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </View>

        <View style={styles.row}>
          <Field label="Short Description" value={form.short_description} onChangeText={(v) => setForm({ ...form, short_description: v })} multiline />
          <Field label="Full Description" value={form.item_description} onChangeText={(v) => setForm({ ...form, item_description: v })} multiline />
        </View>

        {message ? <Text style={[styles.message, message.includes("success") ? styles.successMessage : styles.errorMessage]}>{message}</Text> : null}

        <Toolbar>
          <Button icon={Save} onPress={save} disabled={saving} loading={saving}>{form.id ? "Update Product" : "Save Product"}</Button>
          {form.id ? <Button variant="secondary" onPress={() => { setForm(empty); setMessage(""); setCodeMessage(""); setCodeManualEdit(false); }}>Cancel Edit</Button> : null}
        </Toolbar>
      </Card>

      <Card style={{ padding: 16 }}>
        <Toolbar>
          <Field label="Search Catalog" value={q} onChangeText={setQ} placeholder="Product code, item name, brand..." style={{ flex: 2 }} />
          <Button icon={Search} onPress={load}>Search Products</Button>
        </Toolbar>
      </Card>

      <DataTable
        actionsWidth={56}
        columns={[
          { key: "item_code", label: "Product Code", flex: 1.1, style: { fontWeight: "700" } },
          { key: "item_name", label: "Item Name", flex: 2.2 },
          { key: "main_category_name", label: "Category", flex: 1.3 },
          { key: "sub_category_name", label: "Sub Category", flex: 1.3 },
          { key: "unit", label: "Unit", flex: 0.7 },
          { key: "unit_price", label: "Sale Price (PKR)", flex: 1.2, render: (r) => `PKR ${Number(r.unit_price || 0).toFixed(2)}` },
          {
            key: "scope",
            label: "Scope",
            flex: 0.9,
            render: (r) => (
              <Badge variant={!r.created_by_user_id ? "secondary" : "info"}>
                {!r.created_by_user_id ? "GLOBAL" : (Number(r.created_by_user_id) === Number(user?.id) ? "MY ACCOUNT" : "ACCOUNT")}
              </Badge>
            )
          },
          {
            key: "status",
            label: "Status",
            flex: 0.8,
            render: (r) => (
              <Badge variant={r.status === "active" ? "success" : "danger"}>
                {String(r.status || "active").toUpperCase()}
              </Badge>
            )
          }
        ]}
        rows={rows}
        renderActions={(row) => {
          const isOwner = Number(row.created_by_user_id) === Number(user?.id);
          const canManage = isAdmin || isOwner;
          if (!canManage) return null;
          return (
            <ActionMenu
              actions={[
                { label: "Edit Product", icon: Edit3, onPress: () => beginEdit(row) },
                { label: "Delete Product", icon: Trash2, variant: "danger", onPress: () => remove(row.id) }
              ]}
            />
          );
        }}
      />
    </View>
  );
}
